import { Capacitor } from '@capacitor/core'

/**
 * Returns true if Capacitor Local Notifications are available (native platform).
 */
export function isLocalNotificationsAvailable() {
  return Capacitor.isNativePlatform()
}

/**
 * Requests notification permission on native platforms.
 * Returns 'granted' or 'denied'.
 */
export async function requestLocalNotificationPermission() {
  if (!isLocalNotificationsAvailable()) return 'denied'

  const { LocalNotifications } = await import('@capacitor/local-notifications')
  let permStatus = await LocalNotifications.checkPermissions()
  if (permStatus.display === 'prompt') {
    permStatus = await LocalNotifications.requestPermissions()
  }
  return permStatus.display
}

/**
 * Checks the current permission state without prompting.
 * Returns 'granted', 'denied', or 'prompt'.
 */
export async function checkLocalNotificationPermission() {
  if (!isLocalNotificationsAvailable()) return 'denied'

  const { LocalNotifications } = await import('@capacitor/local-notifications')
  const permStatus = await LocalNotifications.checkPermissions()
  return permStatus.display
}

/**
 * Builds task notification objects for a given schedule day.
 * Returns the array without scheduling — caller batches with other notification types.
 */
export function buildTaskNotifications(scheduleDate, scheduleItems, tasks) {
  const now = new Date()
  const notifications = []

  for (const item of scheduleItems) {
    if (item.type !== 'task') continue

    const [h, m] = item.time.split(':').map(Number)
    const fireDate = new Date(`${scheduleDate}T00:00:00`)
    fireDate.setHours(h, m, 0, 0)

    if (fireDate <= now) continue

    const parentTask = tasks.find((t) => t.id === item.taskId)
    const subtask = parentTask?.subtasks?.find((s) => s.id === item.subtaskId)
    if (subtask?.done) continue

    const parentTitle = parentTask?.title || ''
    const [eh, em] = item.endTime.split(':').map(Number)
    const eh12 = eh % 12 || 12
    const eampm = eh >= 12 ? 'PM' : 'AM'

    notifications.push({
      id: generateNotificationId(item),
      title: `Time to start: ${item.title}`,
      body: `${parentTitle} — until ${eh12}:${String(em).padStart(2, '0')} ${eampm}`,
      schedule: { at: fireDate },
      sound: 'default',
      extra: {
        taskId: item.taskId,
        subtaskId: item.subtaskId,
      },
    })
  }

  return notifications
}

const TIME_OF_DAY_HOURS = {
  morning: { hour: 8, minute: 0 },
  afternoon: { hour: 13, minute: 0 },
  evening: { hour: 20, minute: 0 },
}

// Action category attached to medication reminders so iOS shows snooze buttons.
export const MEDICATION_ACTION_TYPE_ID = 'MEDICATION_REMINDER'

// Snooze buttons offered on a medication reminder. iOS shows up to ~4 actions on
// an expanded notification; these are ordered shortest-first.
export const SNOOZE_OPTIONS = [
  { id: 'snooze-15', title: 'Snooze 15 min', minutes: 15 },
  { id: 'snooze-60', title: 'Snooze 1 hour', minutes: 60 },
  { id: 'snooze-180', title: 'Snooze 3 hours', minutes: 180 },
]

/**
 * Registers the medication reminder action category (snooze buttons) with iOS.
 * Must run before notifications carrying MEDICATION_ACTION_TYPE_ID are delivered,
 * so call it once at startup. No-op on web.
 */
export async function registerMedicationActionTypes() {
  if (!isLocalNotificationsAvailable()) return

  const { LocalNotifications } = await import('@capacitor/local-notifications')
  await LocalNotifications.registerActionTypes({
    types: [
      {
        id: MEDICATION_ACTION_TYPE_ID,
        actions: SNOOZE_OPTIONS.map((o) => ({ id: o.id, title: o.title })),
      },
    ],
  })
}

/**
 * Parses an "HH:MM" 24-hour string into { hour, minute }.
 * Returns null for empty/malformed input so callers can fall back to a preset.
 */
function parseReminderTime(str) {
  if (typeof str !== 'string') return null
  const match = /^(\d{1,2}):(\d{2})$/.exec(str.trim())
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null
  return { hour, minute }
}

/**
 * Formats a Date as a local YYYY-MM-DD string (not UTC — avoids off-by-one
 * near midnight that toISOString() would introduce).
 */
function toLocalDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * Builds medication reminders for `numDays` starting at `startDate` (a Date).
 * Recurring med times are fully known in advance, so we queue many days ahead —
 * this is what lets reminders fire when the app hasn't been opened in a while.
 */
export function buildMedicationNotificationsForDays(medications, startDate, numDays) {
  const all = []
  for (let i = 0; i < numDays; i++) {
    const d = new Date(startDate)
    d.setDate(d.getDate() + i)
    all.push(...buildMedicationNotifications(medications, toLocalDateStr(d)))
  }
  return all
}

/**
 * Builds medication reminder notification objects for a given date.
 * Uses the med's exact `reminderTime` ("HH:MM") when set; otherwise falls back
 * to the timeOfDay preset clock times (8 AM / 1 PM / 8 PM).
 * Skips notifications whose fire time has already passed.
 */
export function buildMedicationNotifications(medications, dateStr) {
  const now = new Date()
  const notifications = []

  for (const med of medications) {
    const time = parseReminderTime(med.reminderTime)
      || TIME_OF_DAY_HOURS[med.timeOfDay]
      || TIME_OF_DAY_HOURS.morning
    const fireDate = new Date(`${dateStr}T00:00:00`)
    fireDate.setHours(time.hour, time.minute, 0, 0)

    if (fireDate <= now) continue

    notifications.push({
      id: generateMedNotificationId(med.id, dateStr),
      title: 'Medication reminder',
      body: `Time to take ${med.name}`,
      schedule: { at: fireDate },
      sound: 'default',
      actionTypeId: MEDICATION_ACTION_TYPE_ID,
      extra: { medicationId: med.id, type: 'medication' },
    })
  }

  return notifications
}

/**
 * Builds one-off snoozed medication reminders from a persisted snooze map
 * (`{ medId: snoozeUntilEpochMs }`). These must be rebuilt on every reschedule
 * because scheduleAllNotifications cancels and rebuilds the whole pending set —
 * an ad-hoc snooze notification would otherwise be wiped on the next rebuild.
 * Skips meds with no active snooze and snoozes whose time has already passed.
 */
export function buildSnoozeNotifications(medications, snoozeUntilByMedId, now = new Date()) {
  const notifications = []
  if (!snoozeUntilByMedId) return notifications

  for (const med of medications) {
    const until = snoozeUntilByMedId[med.id]
    if (!until) continue
    const fireDate = new Date(until)
    if (fireDate <= now) continue

    notifications.push({
      id: generateSnoozeNotificationId(med.id),
      title: 'Medication reminder',
      body: `Time to take ${med.name}`,
      schedule: { at: fireDate },
      sound: 'default',
      actionTypeId: MEDICATION_ACTION_TYPE_ID,
      extra: { medicationId: med.id, type: 'medication', snoozed: true },
    })
  }

  return notifications
}

/**
 * Returns a snooze map with expired entries (snoozeUntil <= nowMs) removed.
 * Returns the SAME object reference when nothing changed, so callers can cheaply
 * detect whether a persist is needed.
 */
export function pruneExpiredSnoozes(snoozeUntilByMedId, nowMs) {
  if (!snoozeUntilByMedId) return {}
  const entries = Object.entries(snoozeUntilByMedId)
  const kept = entries.filter(([, until]) => until > nowMs)
  if (kept.length === entries.length) return snoozeUntilByMedId
  return Object.fromEntries(kept)
}

// iOS keeps at most 64 pending local notifications per app; anything beyond
// that is silently dropped by the OS. We budget explicitly so the *soonest*
// reminders always survive rather than leaving it to chance.
export const IOS_MAX_PENDING = 64

/**
 * Cancels all pending notifications, then schedules the provided combined array.
 * When more than IOS_MAX_PENDING are supplied, keeps the soonest ones (by fire
 * time) so near-term reminders are never crowded out by far-future ones.
 * Returns the number actually scheduled.
 */
export async function scheduleAllNotifications(allNotifications) {
  if (!isLocalNotificationsAvailable()) return 0

  const { LocalNotifications } = await import('@capacitor/local-notifications')

  const pending = await LocalNotifications.getPending()
  if (pending.notifications.length > 0) {
    await LocalNotifications.cancel({ notifications: pending.notifications })
  }

  // Sort by fire time ascending and keep only what iOS will actually hold.
  const sorted = [...allNotifications].sort(
    (a, b) => a.schedule.at.getTime() - b.schedule.at.getTime()
  )
  const budgeted = sorted.slice(0, IOS_MAX_PENDING)

  if (budgeted.length > 0) {
    await LocalNotifications.schedule({ notifications: budgeted })
  }
  return budgeted.length
}

/**
 * Schedules local notifications for schedule items.
 * Cancels all previously scheduled notifications first, then schedules
 * fresh ones for every upcoming task item on the given date.
 * Kept for backward compatibility — the combined coordinator in
 * NotificationContext is preferred.
 */
export async function scheduleNotificationsForDay(scheduleDate, scheduleItems, tasks) {
  if (!isLocalNotificationsAvailable()) return
  const taskNotifs = buildTaskNotifications(scheduleDate, scheduleItems, tasks)
  await scheduleAllNotifications(taskNotifs)
}

/**
 * Cancels all scheduled local notifications.
 */
export async function cancelAllLocalNotifications() {
  if (!isLocalNotificationsAvailable()) return

  const { LocalNotifications } = await import('@capacitor/local-notifications')
  const pending = await LocalNotifications.getPending()
  if (pending.notifications.length > 0) {
    await LocalNotifications.cancel({ notifications: pending.notifications })
  }
}

/**
 * Adds listeners for local notification events.
 */
export async function addLocalNotificationListeners(handlers = {}) {
  if (!isLocalNotificationsAvailable()) return

  const { LocalNotifications } = await import('@capacitor/local-notifications')

  if (handlers.onReceived) {
    await LocalNotifications.addListener('localNotificationReceived', handlers.onReceived)
  }
  if (handlers.onActionPerformed) {
    await LocalNotifications.addListener('localNotificationActionPerformed', handlers.onActionPerformed)
  }
}

/**
 * Generates a stable integer ID for a notification from a schedule item.
 */
function generateNotificationId(item) {
  const key = `${item.time}-${item.taskId}-${item.subtaskId}`
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

/**
 * Generates a stable integer ID for a medication notification.
 */
function generateMedNotificationId(medId, dateStr) {
  const key = `med-${medId}-${dateStr}`
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

/**
 * Generates a stable integer ID for a snoozed medication notification.
 * Stable per med (not per snooze time) so re-snoozing the same med replaces its
 * pending reminder rather than stacking a second one.
 */
function generateSnoozeNotificationId(medId) {
  const key = `med-snooze-${medId}`
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}
